/**
 * EditInquiryNameDialog
 *
 * Agent Progress 页面专用 — 名称编辑受控弹窗。
 *
 * 与原 contact-progress 内联实现 1:1 行为：
 *  - Input 预填当前名称
 *  - Save 在 trim 后空字符串时 disabled
 *  - 成功/失败由 onSubmit 调用方决定（成功通常关闭、失败通常保留）
 *
 * 不直接调用任何 API；纯受控展示组件，便于复用与测试。
 */

import { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from '@leadsnavi/ui-kit';
import type { EditInquiryNameDialogProps } from '../../types';

export const EditInquiryNameDialog: FC<EditInquiryNameDialogProps> = ({
  open,
  defaultName,
  loading,
  onOpenChange,
  onSubmit,
}) => {
  const [value, setValue] = useState(defaultName);

  // 弹窗每次打开时同步默认名称（避免上一次输入残留）
  useEffect(() => {
    if (open) {
      setValue(defaultName);
    }
  }, [open, defaultName]);

  const trimmed = value.trim();
  const saveDisabled = trimmed.length === 0 || loading;

  const handleSave = async () => {
    if (saveDisabled) return;
    try {
      await onSubmit(trimmed);
    } catch {
      // 失败由调用方决定弹窗去留 + toast；这里不重复处理
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-120 p-4 pt-5" onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-left leading-[24px] -mt-2">Edit inquiry name</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Input placeholder="Enter inquiry name" value={value} onChange={e => setValue(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button disabled={saveDisabled} loading={loading} onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditInquiryNameDialog;
